import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Server, Database, Upload, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

export default function SystemAdminPage({ onLogout, userRole, isFeatureEnabled }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importType, setImportType] = useState("machines");
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [templates, setTemplates] = useState({});

  const fetch = useCallback(async () => {
    try {
      const [sR, tR] = await Promise.allSettled([
        axios.get(`${API}/system-admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/bulk-import/templates`, { headers: getAuthHeaders() }),
      ]);
      if (sR.status === "fulfilled") setStats(sR.value.data);
      if (tR.status === "fulfilled") setTemplates(tR.value.data || {});
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleImport = async () => {
    if (!importFile) { toast.error("Select a CSV file"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const resp = await axios.post(`${API}/bulk-import/${importType}`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      setImportResult(resp.data);
      toast.success(`Imported ${resp.data.imported} records`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const downloadTemplate = (type) => {
    const schema = templates[type];
    if (!schema) return;
    const header = schema.columns.join(",");
    const blob = new Blob([header + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${type}-template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  const dbInfo = stats?.database || {};
  const collections = stats?.collections || [];

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="system-admin-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <PageHeader title="System Administration" subtitle="Database stats, bulk import, system health" testId="sysadmin-title" />
          <Button onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none text-xs font-bold" data-testid="bulk-import-btn"><Upload size={14} className="mr-1" /> Bulk Import</Button>
        </div>

        {/* DB Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="db-stats">
          {[["Total Records", dbInfo.total_records, "#0F172A"], ["Collections", dbInfo.total_collections, "#2563EB"], ["Data Size", `${dbInfo.data_size_mb} MB`, "#10B981"], ["Storage", `${dbInfo.storage_size_mb} MB`, "#D97706"], ["Indexes", `${dbInfo.index_size_mb} MB`, "#7C3AED"]].map(([l, v, c]) => (
            <div key={l} className="bg-white border border-[#E2E8F0] px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{l}</p><p className="text-2xl font-heading font-black mt-0.5" style={{ color: c }}>{v}</p></div>
          ))}
        </div>

        {/* Collections Table */}
        <div className="bg-white border border-[#E2E8F0]" data-testid="collections-table">
          <div className="px-4 py-2.5 bg-[#FAFAFA] border-b border-[#E2E8F0] flex items-center gap-2"><Database size={14} className="text-[#64748B]" /><span className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Collections ({collections.length})</span></div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
                <tr><th className="px-4 py-2 text-xs font-bold text-[#0F172A] uppercase text-left">Collection</th><th className="px-4 py-2 text-xs font-bold text-[#0F172A] uppercase text-right">Records</th><th className="px-4 py-2 text-xs font-bold text-[#0F172A] uppercase text-right">Bar</th></tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {collections.map(c => {
                  const maxCount = collections[0]?.count || 1;
                  const pct = (c.count / maxCount) * 100;
                  return (
                    <tr key={c.name} className="hover:bg-[#FAFAFA]">
                      <td className="px-4 py-2 font-mono text-xs text-[#0F172A]">{c.name}</td>
                      <td className="px-4 py-2 text-xs text-right font-bold text-[#0F172A]">{c.count.toLocaleString()}</td>
                      <td className="px-4 py-2 w-40"><div className="w-full h-2 bg-[#F1F5F9]"><div className="h-full bg-[#2563EB]" style={{ width: `${Math.max(pct, 1)}%` }} /></div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bulk Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-lg" data-testid="import-dialog">
          <DialogHeader><DialogTitle>Bulk CSV Import</DialogTitle><DialogDescription className="text-xs text-[#64748B]">Upload a CSV file to bulk import records.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-[10px] font-bold uppercase text-[#64748B]">Entity Type</Label>
              <div className="flex gap-2 mt-1">
                {Object.keys(templates).map(t => (
                  <button key={t} onClick={() => setImportType(t)} className={`px-3 py-1.5 text-xs font-bold border ${importType === t ? "bg-[#0F172A] text-white border-[#0F172A]" : "text-[#64748B] border-[#E2E8F0]"}`} data-testid={`import-type-${t}`}>{t}</button>
                ))}
              </div>
            </div>
            {templates[importType] && (
              <div className="text-[10px] text-[#64748B]">
                <p><strong>Required:</strong> {templates[importType].required.join(", ")}</p>
                <p><strong>Optional:</strong> {templates[importType].optional.join(", ")}</p>
                <Button size="sm" variant="outline" onClick={() => downloadTemplate(importType)} className="rounded-none text-[10px] mt-1" data-testid="download-template"><Download size={11} className="mr-1" />Download Template CSV</Button>
              </div>
            )}
            <div>
              <Label className="text-[10px] font-bold uppercase text-[#64748B]">CSV File</Label>
              <Input type="file" accept=".csv" onChange={e => setImportFile(e.target.files[0])} className="mt-1 rounded-none text-xs" data-testid="import-file" />
            </div>
            <Button onClick={handleImport} disabled={importing || !importFile} className="w-full bg-[#2563EB] text-white rounded-none text-xs font-bold" data-testid="import-submit-btn">
              {importing ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileSpreadsheet size={14} className="mr-1" />}
              {importing ? "Importing..." : `Import ${importType}`}
            </Button>
            {importResult && (
              <div className={`p-3 border text-xs ${importResult.errors.length > 0 ? "border-[#FDE68A] bg-[#FFFBEB]" : "border-[#BBF7D0] bg-[#F0FDF4]"}`} data-testid="import-result">
                <p className="font-bold">Imported: {importResult.imported} / {importResult.total_rows} rows</p>
                {importResult.errors.length > 0 && <div className="mt-1 text-[#DC2626]">{importResult.errors.slice(0, 5).map((e, i) => <p key={i}>{e}</p>)}{importResult.errors.length > 5 && <p>+{importResult.errors.length - 5} more errors</p>}</div>}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
